import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronLeft, FaAnglesRight } from "react-icons/fa6";
import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";
import { postCompleteGuide } from "../../api/mainApi";

const Guide = ({ setGuideCompleted }) => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);

  // Допоміжна функція для отримання контенту (масиву або одного рядка)
  const getContent = (key) => {
    const content = t(key, { returnObjects: true });
    // Якщо раптом переклад не знайдено або повернеться рядок, робимо масив для безпеки
    return Array.isArray(content) ? content : [content];
  };

  const slides = [
    {
      title: t("guide.intro.title"),
      contentImage: "/logo.webp",
      isLogo: true,
      // Ми тепер просто передаємо ключ, а не хардкодимо масив тут
      content: getContent("guide.intro.content"),
      backgroundImage: "/guide-bg.webp",
    },
    {
      title: t("guide.freeze.title"),
      contentImage: "/guide-1.webp",
      content: getContent("guide.freeze.content"),
      backgroundImage: "/guide-bg-1.webp",
    },
    {
      title: t("guide.mood.title"),
      contentImage: "/guide-2.webp",
      content: getContent("guide.mood.content"),
      backgroundImage: "/guide-bg-3.webp",
    },
    {
      title: t("guide.aiAnalysis.title"),
      contentImage: "/guide-3.webp",
      content: getContent("guide.aiAnalysis.content"),
      backgroundImage: "/guide-bg-2.webp",
    },
    {
      title: t("guide.aiPsychologist.title"),
      contentImage: "/guide-4.webp",
      content: getContent("guide.aiPsychologist.content"),
      backgroundImage: "/guide-bg-4.webp",
    },
    {
      title: t("guide.statistics.title"),
      contentImage: "/guide-5.webp",
      content: getContent("guide.statistics.content"),
      backgroundImage: "/guide-bg-5.webp",
    },
  ];

  const [sliderRef, instanceRef] = useKeenSlider({
    loop: false,
    slides: { perView: 1 },
    mode: "snap",
    rubberband: false,
    slideChanged: (slider) => setCurrentSlide(slider.track.details.rel),
  });

  const handleSkip = async () => {
    await postCompleteGuide();
    localStorage.setItem("guideCompleted", true);
    setGuideCompleted(true);
  };

  const handleNext = async () => {
    if (currentSlide === slides.length - 1) {
      await postCompleteGuide();
      localStorage.setItem("guideCompleted", true);
      setGuideCompleted(true);
    } else {
      instanceRef.current?.next();
    }
  };

  return (
    <div className="relative w-full h-full flex flex-col items-center pb-4 bg-gray-950 overflow-hidden">
      {/* Skip Button */}
      <button
        onClick={handleSkip}
        className="fixed z-50 top-20 right-4 p-2 rounded-full bg-slate-950/50 text-white"
      >
        <FaAnglesRight className="h-5 w-5" />
      </button>

      {/* Slider */}
      <div ref={sliderRef} className="keen-slider w-full flex-1">
        {slides.map((slide, index) => (
          <div
            key={index}
            className="keen-slider__slide w-full h-full flex flex-col relative"
          >
            {/* Background */}
            <div className="absolute inset-0 z-0">
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url('${slide.backgroundImage}')` }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/80 to-transparent" />
            </div>

            {/* Content */}
            <div className="relative z-10 flex flex-col h-full px-4 pt-20">
              <h2 className="text-start text-2xl font-bold mb-6 me-8 bg-clip-text text-transparent bg-gradient-to-r from-cyan-500 to-emerald-500 drop-shadow-sm">
                {slide.title}
              </h2>

              {slide.contentImage && (
                <div className="w-full h-48 sm:h-56 shrink-0 mb-6 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm flex items-center justify-center p-4 shadow-lg">
                  <img
                    src={slide.contentImage}
                    alt=""
                    className={`object-contain drop-shadow-xl ${
                      slide.isLogo ? "h-20 w-auto" : "w-full h-full"
                    }`}
                  />
                </div>
              )}

              {/* Text Loop */}
              <div className="flex flex-col gap-3 pb-4">
                {slide.content.map((item, idx) => (
                  <p
                    key={idx}
                    className="text-sm sm:text-base font-medium leading-relaxed text-start text-gray-200"
                  >
                    {item}
                  </p>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Buttons */}

      <div className="flex justify-between w-full pt-4 gap-4 px-4 bg-gray-950">
        <button
          onClick={() => instanceRef.current?.prev()}
          disabled={currentSlide === 0}
          className="py-2 px-4 bg-gray-800 rounded-lg disabled:opacity-50"
        >
          <FaChevronLeft />
        </button>

        <button
          onClick={handleNext}
          className="flex-1 py-2 text-3xl font-bold rounded-lg bg-gradient-to-tr from-emerald-500 via-cyan-500 to-emerald-500 bg-gradient-size animate-gradient-move"
        >
          {t(
            currentSlide === slides.length - 1
              ? "guide.complete"
              : "guide.next",
          )}
        </button>
      </div>
    </div>
  );
};

export default Guide;
